import { useCallback, useEffect, useState } from "react";
import { listParents } from "../lib/admin.js";
import AdminSidebar from "./AdminSidebar.jsx";

const PAGE_SIZE = 50;

function formatDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
}

function timeAgo(iso) {
  if (!iso) return "-";
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "방금 전";
  if (mins < 60) return `${mins}분 전`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}시간 전`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}일 전`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}개월 전`;
  const years = Math.floor(months / 12);
  return `${years}년 전`;
}

function formatKRW(n) {
  if (n == null) return "-";
  return n.toLocaleString() + "원";
}

export default function AdminParents({ onSignOut }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(0); }, [debouncedSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { rows, total } = await listParents({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        search: debouncedSearch,
      });
      setRows(rows);
      setTotal(total);
    } catch (err) {
      setError(err.message ?? "불러오기 실패");
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  const hasPrev = page > 0;
  const hasNext = (page + 1) * PAGE_SIZE < total && rows.length === PAGE_SIZE;

  return (
    <div className="admin">
      <AdminSidebar active="parents" onSignOut={onSignOut}/>

      <main className="admin-main">
        <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:6,flexWrap:'wrap',gap:12}}>
          <div>
            <h1 className="admin-h1">👥 보호자</h1>
            <p className="admin-sub">
              가입한 보호자 전체 {total}명. 최근 활동 순으로 정렬.
            </p>
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="🔍 이메일 / 이름"
              style={{
                padding:'8px 14px',fontSize:13,borderRadius:9999,
                border:'1px solid #e2e8f0',background:'#fff',
                fontFamily:'inherit',minWidth:240,
              }}
            />
            <button className="btn btn-outline" style={{padding:'8px 16px',fontSize:12}} onClick={load}>
              ⟳ 새로고침
            </button>
          </div>
        </div>

        {error && (
          <div style={{background:'#fef9f0',border:'1px solid #e74c3c44',borderRadius:12,padding:'12px 14px',marginBottom:16,fontSize:13,color:'#c0392b'}}>
            보호자 목록을 불러오지 못했어요: {error}
          </div>
        )}

        <div className="admin-table-wrap">
          <div className="parents-row head">
            <div>보호자</div>
            <div>자녀</div>
            <div>케이스</div>
            <div>발행</div>
            <div>잔여 분석권</div>
            <div>누적 결제</div>
            <div>최근 활동</div>
            <div></div>
          </div>
          {loading ? (
            <div className="parents-row">
              <div style={{gridColumn:'1 / -1',textAlign:'center',padding:'40px 0'}}>
                <div className="spinner" style={{display:'inline-block'}}/>
              </div>
            </div>
          ) : rows.length === 0 ? (
            <div className="parents-row">
              <div style={{gridColumn:'1 / -1',textAlign:'center',padding:'40px 0',color:'#718096',fontSize:13}}>
                {debouncedSearch
                  ? `"${debouncedSearch}"에 해당하는 보호자가 없어요.`
                  : "아직 가입한 보호자가 없어요."}
              </div>
            </div>
          ) : (
            rows.map((r) => (
              <div key={r.id} className="parents-row" style={{cursor:'pointer'}}
                onClick={() => { window.location.hash = `#admin/parents/${r.id}`; }}>
                <div style={{minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:13,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                    {r.display_name || r.email}
                  </div>
                  {r.display_name && (
                    <div style={{fontSize:11,color:'#718096',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                      {r.email}
                    </div>
                  )}
                </div>
                <div style={{fontVariantNumeric:'tabular-nums'}}>👶 {r.children_count}</div>
                <div style={{fontVariantNumeric:'tabular-nums'}}>{r.cases_count}</div>
                <div style={{fontVariantNumeric:'tabular-nums',fontWeight:700,color:r.done_count>0?'#1f7a3f':'#a0aec0'}}>
                  {r.done_count}
                </div>
                <div style={{fontVariantNumeric:'tabular-nums',fontWeight:700,color:r.credits_remaining>0?'#2c5f8d':'#a0aec0'}}>
                  🎟 {r.credits_remaining}
                </div>
                <div style={{fontVariantNumeric:'tabular-nums',color:'#4a5568'}}>{formatKRW(r.total_paid)}</div>
                <div style={{fontSize:11,color:'#718096'}}>{timeAgo(r.last_activity)}</div>
                <div style={{color:'#2c5f8d',fontSize:14}}>→</div>
              </div>
            ))
          )}
        </div>

        {(hasPrev || hasNext) && (
          <div style={{display:'flex',justifyContent:'center',gap:8,marginTop:16}}>
            <button
              className="btn btn-outline"
              style={{padding:'8px 16px',fontSize:12,opacity: hasPrev ? 1 : 0.4}}
              disabled={!hasPrev}
              onClick={() => setPage((p) => Math.max(0, p - 1))}>
              ← 이전
            </button>
            <div style={{padding:'8px 14px',fontSize:12,color:'#718096'}}>
              {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + rows.length} / {total}
            </div>
            <button
              className="btn btn-outline"
              style={{padding:'8px 16px',fontSize:12,opacity: hasNext ? 1 : 0.4}}
              disabled={!hasNext}
              onClick={() => setPage((p) => p + 1)}>
              다음 →
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
